/**
 * The two pieces of the lockup, as pictures.
 *
 * The LEGENDS wordmark is CHROME — a bevelled metal ramp with a highlight
 * running across the letterforms — and the L badge is a cut shield with the
 * heat ramp behind it. Neither is type and neither is a flat SVG: §04 calls a
 * single-colour rendering of the wordmark a dead lockup, and §15 forbids
 * recolouring the ramp. So they are raster assets, cut once by
 * `scripts/build-brand-assets.js` from the brand sheets and served at the widths
 * this file names.
 *
 * The WIDTHS are the contract between this component and that script, which is
 * why they are exported rather than inlined: a rung added there without a rung
 * added here is a file nobody ever requests, and a rung named here without a
 * file behind it is a 404 in the srcset the browser picks silently.
 *
 * `alt` is a required decision rather than a default, because the same picture
 * is decorative in one place and load-bearing in another. In the header the
 * lockup sits inside a link that already carries its own accessible name, so
 * announcing the wordmark again would say the product's name twice; on the
 * splash the wordmark IS the screen, and it has to speak.
 */

/**
 * The ladders, and they are a CONTRACT with `scripts/build-brand-assets.js`
 * rather than a preference: a rung named here without a file behind it is a 404
 * the browser picks silently, and a rung written there without one here is a
 * file nobody ever requests. If the script's ladder moves, these move with it.
 *
 * The wordmark runs to 1440 because the splash scales it to the full width of a
 * phone; the badge to 480 because the same sheet feeds the app icons.
 */
export const WORDMARK_WIDTHS = [480, 960, 1440];

export const BADGE_WIDTHS = [120, 240, 480];

const BRAND_DIR = '/assets/brand';

function srcSetOf(stem, widths) {
    return widths.map((width) => `${BRAND_DIR}/${stem}-${width}.webp ${width}w`).join(', ');
}

/**
 * The smallest rung as the `src`.
 *
 * A browser that ignores `srcset` — and the one that matters is an old in-app
 * webview, which is exactly where a shared link lands — gets the cheapest file
 * rather than the largest. It will look soft; it will not cost a megabyte.
 */
function fallbackOf(stem, widths) {
    return `${BRAND_DIR}/${stem}-${widths[0]}.webp`;
}

/**
 * `sizes` is the caller's, because only the caller knows the box. Passing the
 * layout width in CSS terms is what lets the browser choose a rung BEFORE the
 * stylesheet has resolved, which is the whole point of the attribute.
 */
export function BrandWordmark({ alt = '', sizes = '200px', className = '', ...rest }) {
    return (
        <img
            className={className}
            src={fallbackOf('legends-wordmark', WORDMARK_WIDTHS)}
            srcSet={srcSetOf('legends-wordmark', WORDMARK_WIDTHS)}
            sizes={sizes}
            alt={alt}
            decoding="async"
            /* Intrinsic ratio, so the row does not reflow when the picture
               lands. The numbers are the source sheet's proportion, not a rung:
               they are a RATIO and the CSS sets the real size. */
            width="960"
            height="200"
            {...rest}
        />
    );
}

export function BrandBadge({ alt = '', sizes = '96px', className = '', ...rest }) {
    return (
        <img
            className={className}
            src={fallbackOf('legends-badge', BADGE_WIDTHS)}
            srcSet={srcSetOf('legends-badge', BADGE_WIDTHS)}
            sizes={sizes}
            alt={alt}
            decoding="async"
            width="216"
            height="216"
            {...rest}
        />
    );
}
